'use client';

import { useCallback, useEffect, useState } from 'react';
import { Info } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  getAttributionSettings,
  setAttributionSettings,
} from '@/apis/attributionSettings';

interface AttributionSettingsCardProps {
  orgUUID: string;
  /** Only admins may flip the switch; others see the current state read-only. */
  isAdmin: boolean;
}

/**
 * Org-level "Strict ticket attribution" toggle (Settings → Code Management).
 *
 * ON = facts-only books: a task's project counts only when it came from a
 * linked tracker ticket (PR↔Linear link or a ticket URL in the session);
 * everything else appears under one "Unallocated" bucket on the Outcomes and
 * Budgeting pages instead of an AI-inferred project. The backend flips within
 * a minute (TTL-cached flag) — no reingest, instantly reversible.
 */
export default function AttributionSettingsCard({
  orgUUID,
  isAdmin,
}: AttributionSettingsCardProps) {
  const [strict, setStrict] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await getAttributionSettings(orgUUID);
      if (!cancelled && res.success) {
        setStrict(Boolean(res.strictTicketAttribution));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgUUID]);

  const onToggle = useCallback(
    async (next: boolean) => {
      if (!isAdmin || saving) return;
      setSaving(true);
      setError(null);
      const previous = strict;
      setStrict(next); // optimistic — the write is cheap and reversible
      const res = await setAttributionSettings(orgUUID, next);
      if (!res.success) {
        setStrict(previous);
        setError('Could not save — try again.');
      }
      setSaving(false);
    },
    [isAdmin, saving, strict, orgUUID]
  );

  return (
    <div className="bg-ds-surface-white rounded-lg border border-ds-border-subtlest p-4 mb-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-ds-text-darker">
              Strict ticket attribution
            </span>
            <TooltipProvider delay={200}>
              <Tooltip>
                <TooltipTrigger
                  render={
                    <span className="cursor-default inline-flex">
                      <Info size={14} className="text-ds-text-subtle/70" />
                    </span>
                  }
                />
                <TooltipContent
                  side="top"
                  className="max-w-xs py-2 px-3 bg-ds-surface-white text-ds-text-darker border border-ds-border-subtlest rounded-md shadow-lg"
                >
                  Attribution becomes facts-only: spend counts toward a project
                  only when the work is linked to a tracker ticket (a PR linked
                  in Linear, or a ticket URL in the session). Everything else
                  shows as &quot;Unallocated&quot; instead of an AI-suggested
                  project. Takes effect within a minute; instantly reversible.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <p className="mt-1 text-xs text-ds-text-subtle">
            Only count spend that&apos;s hard-matched to tickets — everything
            else appears as Unallocated on Outcomes and Budgeting.
          </p>
          {error && (
            <p className="mt-1 text-xs text-ds-danger-default">{error}</p>
          )}
        </div>
        <Switch
          checked={Boolean(strict)}
          disabled={!isAdmin || strict === null || saving}
          onCheckedChange={onToggle}
          aria-label="Strict ticket attribution"
        />
      </div>
    </div>
  );
}
